import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
} from "@mui/material";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

function pad(n) {
  return String(n).padStart(2, "0");
}

function toGCalDateUTC(d) {
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

function buildGoogleCalendarUrl(params) {
  const url = new URL("https://calendar.google.com/calendar/render");
  url.searchParams.set("action", "TEMPLATE");
  url.searchParams.set("text", params.title);

  const dates = `${toGCalDateUTC(params.start)}/${toGCalDateUTC(params.end)}`;
  url.searchParams.set("dates", dates);

  url.searchParams.set("details", params.details);
  if (params.location) url.searchParams.set("location", params.location);

  // add=guest@example.com can be repeated in Google Calendar URLs
  (params.guests ?? []).forEach((g) => url.searchParams.append("add", g));

  return url.toString();
}

export default function Contact() {
  const whatsappNumber = "15209871861";
  const whatsappMessage = encodeURIComponent(
    "Hello, I would like to receive more information about your products and services.",
  );
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
  const mapsUrl =
    "https://www.google.com/maps/place/Rustic+World/@32.5457199,-95.8511881,17z/data=!4m8!3m7!1s0x86490cfb001a858d:0xba1cb2e35da7b78d!8m2!3d32.5457154!4d-95.8486132!9m1!1b1!16s%2Fg%2F11g8v3bw84?entry=ttu";

  // ======= Schedule modal state =======
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [duration, setDuration] = useState(30); // minutes

  const businessEmail = "support@rusticworldllc.com";

  const calendarUrl = useMemo(() => {
    const start = new Date();
    start.setDate(start.getDate() + 1);
    start.setHours(15, 0, 0, 0);

    const end = new Date(start.getTime() + duration * 60 * 1000);

    const detailsLines = [
      `Hi Rustic World,`,
      ``,
      `I would like to schedule a video call.`,
      fullName ? `Name: ${fullName}` : null,
      email ? `Email: ${email}` : null,
      note ? `Notes: ${note}` : null,
      ``,
      `Video call: Please add Google Meet (video conferencing) to this event.`,
      `If needed, you can also share a Meet link by email to ${email || "[my email]"}.`,
    ].filter(Boolean);

    return buildGoogleCalendarUrl({
      title: "Rustic World — Video Call",
      details: detailsLines.join("\n"),
      location: "Google Meet (add video conferencing)",
      start,
      end,
      guests: [businessEmail, ...(email ? [email] : [])],
    });
  }, [duration, fullName, email, note]);

  const onOpenCalendar = () => {
    window.open(calendarUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Card
        sx={{
          maxWidth: "100%",
          mx: "auto",
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <CardContent>
          <Box
            mb={1}
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" fontWeight={800} gutterBottom>
              Contact Us
            </Typography>
            <Typography color="text.secondary">
              We’d love to hear from you. Reach out for inquiries or support.
            </Typography>
          </Box>

          <Divider sx={{ mb: 1 }} />

          <Grid
            container
            spacing={4}
            sx={{
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            <Grid item xs={12} md={5}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" gap={2} alignItems="center">
                  <LocationOnIcon color="primary" />
                  <Box>
                    <Typography fontWeight={700}>Address</Typography>
                    <Typography color="text.secondary">
                      1111 TX-243, Canton, TX 75103, United States
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" gap={2} alignItems="center">
                  <PhoneIcon color="primary" />
                  <Box>
                    <Typography fontWeight={700}>Phone</Typography>
                    <Typography color="text.secondary">
                      +1 (520) 987-1861
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" gap={2} alignItems="center">
                  <EmailIcon color="primary" />
                  <Box>
                    <Typography fontWeight={700}>Email</Typography>
                    <Typography color="text.secondary">
                      {businessEmail}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  mt={2}
                  sx={{
                    width: "100%",
                    display: "flex",
                    gap: 2,
                    flexDirection: "column",
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<EmailIcon />}
                    sx={{ borderRadius: 2, width: "100%" }}
                    href={`mailto:${businessEmail}`}
                  >
                    Send Email
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      borderRadius: 2,
                      width: "100%",
                      backgroundColor: "#25D366",
                      "&:hover": { backgroundColor: "#1EBE5D" },
                    }}
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Chat on WhatsApp
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<VideoCallIcon />}
                    sx={{
                      borderRadius: 2,
                      width: "100%",
                    }}
                    onClick={() => setOpen(true)}
                  >
                    Schedule a Video Call
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box
                component="a"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  width: "100%",
                  height: { xs: 300, md: 400 },
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid rgba(36,74,56,0.16)",
                  color: "#15291e",
                  textDecoration: "none",
                  background:
                    "radial-gradient(circle at 78% 20%, rgba(184,103,62,.2), transparent 34%), linear-gradient(135deg, rgba(20,68,43,.94), rgba(239,230,218,.9) 58%, rgba(255,253,248,.96))",
                  boxShadow: "0 24px 60px rgba(24,36,29,.14)",
                  isolation: "isolate",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 18,
                    borderRadius: 3,
                    border: "1px solid rgba(255,255,255,.34)",
                    pointerEvents: "none",
                  },
                  "&::after": {
                    content: '"CANTON"',
                    position: "absolute",
                    right: { xs: -8, md: 10 },
                    bottom: { xs: 10, md: 6 },
                    zIndex: -1,
                    color: "rgba(255,255,255,.16)",
                    fontSize: { xs: 58, md: 92 },
                    fontWeight: 900,
                    letterSpacing: "-.08em",
                    lineHeight: 1,
                  },
                  "&:hover .contact-map-cta": {
                    transform: "translateY(-2px)",
                    backgroundColor: "#b8673e",
                  },
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 999,
                      backgroundColor: "rgba(255,253,248,.86)",
                      color: "#1f5a3d",
                      fontWeight: 900,
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      fontSize: 12,
                    }}
                  >
                    <LocationOnIcon fontSize="small" /> Visit us
                  </Typography>

                  <Typography
                    variant="h4"
                    sx={{
                      mt: 3,
                      maxWidth: 420,
                      color: "#fffdf8",
                      fontWeight: 900,
                      textShadow: "0 2px 18px rgba(0,0,0,.26)",
                    }}
                  >
                    Rustic World in Canton, Texas
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.25,
                      maxWidth: 420,
                      color: "rgba(255,253,248,.9)",
                      fontSize: 18,
                      lineHeight: 1.45,
                    }}
                  >
                    1111 TX-243, Canton, TX 75103
                  </Typography>
                </Box>

                <Box
                  className="contact-map-cta"
                  sx={{
                    alignSelf: "flex-start",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2.5,
                    py: 1.2,
                    borderRadius: 999,
                    backgroundColor: "#244a38",
                    color: "#fffdf8",
                    fontWeight: 900,
                    boxShadow: "0 16px 34px rgba(0,0,0,.2)",
                    transition: "transform 180ms ease, background-color 180ms ease",
                  }}
                >
                  Open in Google Maps <OpenInNewIcon fontSize="small" />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Box textAlign="center">
            <Typography color="text.secondary">
              Business hours: Monday – Sunday, 8:00 AM – 5:00 PM
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Schedule a Video Call
        </DialogTitle>

        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Fill out your details and we’ll open Google Calendar so you can pick
            the best time and add Google Meet.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
            />

            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              fullWidth
            />

            <TextField
              label="What would you like to discuss?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />

            <TextField
              select
              label="Call duration"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              fullWidth
            >
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={45}>45 minutes</MenuItem>
              <MenuItem value={60}>60 minutes</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={onOpenCalendar}
            variant="contained"
            startIcon={<VideoCallIcon />}
          >
            Open Google Calendar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
